import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { GoldCta, IMAGES, scrollToId } from "./shared";

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="hero" className="relative min-h-screen grain overflow-hidden bg-[#080706]">
      <motion.div
        style={{ opacity: glowOpacity }}
        className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        aria-hidden="true"
      >
        <div className="w-full h-full rounded-full bg-[#d4af37]/[0.07] blur-[120px]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] min-h-screen items-center px-5 sm:px-10 pt-24 lg:pt-16 pb-16 gap-10">
        <div className="relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="eyebrow mb-6"
            data-testid="hero-eyebrow"
          >
            Mentorship Cristiana Privata 1:1
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display font-bold uppercase leading-[1.1] text-3xl sm:text-4xl lg:text-5xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-[#fbf9f5]" {...lineAnim(0)}>Domina te stesso.</motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-[#fbf9f5]" {...lineAnim(1)}>Scopri la tua <span className="gold-text">Chiamata</span>.</motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block text-[#fbf9f5]" {...lineAnim(2)}>Costruisci la tua <span className="gold-text">Eredità</span>.</motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="font-quote italic text-lg sm:text-xl text-[#d4af37] mt-5"
            data-testid="hero-subheadline"
          >
            «Non sei chiamato soltanto a sopravvivere, lavorare e pagare bollette.»
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="text-[#b8b2a7] text-base sm:text-lg mt-6 max-w-lg leading-relaxed"
          >
            Sei chiamato a diventare un uomo di fede e disciplina, un leader in casa e fuori, capace di riconoscere i propri talenti, comunicare la propria storia e costruire un futuro allineato al proposito di Dio.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="font-display text-sm sm:text-base tracking-[0.3em] text-[#f3e5ab] mt-8 uppercase"
            data-testid="hero-pillars"
          >
            Cuore <span className="text-[#d4af37]">•</span> Voce <span className="text-[#d4af37]">•</span> Eredità
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <GoldCta testId="hero-cta-primary">Scrivimi in privato su WhatsApp</GoldCta>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("testimonianza")}
              className="text-[11px] uppercase tracking-[0.22em] text-[#b8b2a7] hover:text-[#f3e5ab] underline underline-offset-8 decoration-[#d4af37]/40 transition-colors duration-300 text-left"
            >
              Scopri la mia storia
            </button>
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden border border-[#d4af37]/25 shadow-[0_40px_120px_-40px_rgba(212,175,55,0.25)]"
          >
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="Elio Briotti, fondatore di Uomo Biblico"
              className="w-full h-[420px] sm:h-[520px] lg:h-[620px] object-cover scale-110"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 vignette" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080706] via-transparent to-transparent" />

            <div className="absolute bottom-6 left-6">
              <p className="font-display text-sm tracking-[0.2em] text-[#fbf9f5]">ELIO BRIOTTI</p>
              <p className="text-[11px] text-[#b8b2a7] mt-1">Mentor e Life Coach Cristiano — Fondatore di Fede & Fitness Italia</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
